/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationDetailsPage component
 * -----------------------
 * It fetches and displays detailed information
 * about a specific application based on the ID from URL parameters.
 * Error handling: Displays a simple error message box with a user-friendly
 * message if fetching the data fails (e.g. network error, 404 not found).
 * Loading state shows a spinner while waiting for API response.
 * 
 * For nested objects like organization, domain, and category,
 * it displays their 'name' property instead of the whole object.
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import APIHelper from '../api/APIHelper';
import MessageBox from '../components/MessageBox';
import '../styles/ApplicationDetailsPage.css';
import envConfig from '../config/envConfig';
import ApplicationTablesComponent from '../components/applications/ApplicationTablesComponent';
import FileBrowserComponent from '../components/applications/FileBrowserComponent';
import UpdateApplicationModal from '../components/applications/UpdateApplicationModal';
import RollbackApplicationModal from '../components/applications/RollbackApplicationModal';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEventsEnabledForApplication } from '../utils/eventsAccess';

const statusColors = {
  draft: 'orange',
  published: 'green'
};

const ATTRIBUTES = [
  { key: 'organization', label: 'Organization', icon: 'bi-building' },
  { key: 'domain', label: 'Domain', icon: 'bi-globe' },
  { key: 'category', label: 'Category', icon: 'bi-tag' },
  { key: 'status', label: 'Status', icon: 'bi-circle-fill', colorFromStatus: true, formatter: v => v.charAt(0).toUpperCase() + v.slice(1) },
  { key: 'created_at', label: 'Created At', icon: 'bi-clock-history', formatter: formatDate },
  { key: 'updated_at', label: 'Updated At', icon: 'bi-arrow-repeat', formatter: formatDate }
];

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret
});

export default function ApplicationDetailsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);
  const { showAlertDialog } = useAlertDialog();
  const [showProgress, setShowProgress] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('tables');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);

  const queryClient = useQueryClient();

  const appDetailsQueryKey = ['applicationDetails', id];

  useEffect(() => {
    if (location.state?.force_refresh) {
      queryClient.invalidateQueries(appDetailsQueryKey);
      // clear the state to avoid repeated invalidations
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, queryClient, appDetailsQueryKey, navigate, location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const {
    data: appDetailsData,
    error: appDetailsError,
    isLoading: appDetailsLoading,
    isFetching: appDetailsFetching,
  } = useQuery({
    queryKey: appDetailsQueryKey,
    queryFn: async ({ signal }) => {
      if (!id) return null;
      const url = `/arcee/application/details/${id}/`;
      const options = { method: 'GET', signal };

      const resp = await apiHelper.apiCall(url, options);
      if (!resp.ok) {
        let txt = '';
        try { txt = await resp.text(); } catch (e) { /* ignore */ }
        try {
          const j = txt ? JSON.parse(txt) : null;
          throw new Error((j && (j.error || j.message)) || `Failed to load application (${resp.status})`);
        } catch (e) {
          throw new Error(txt || `Failed to load application (${resp.status})`);
        }
      }
      const json = await resp.json();
      return json;
    },
    enabled: !!id,
    staleTime: 30_000,
    retry: 1,
    onSuccess: (data) => {
      // Ensure prefetch uses exact same key+params that the ApplicationTablesComponent uses.
      // Adjust page / pageSize if your child uses different defaults.
      const page = 1;
      const pageSize = 5; // <-- change to the default pageSize used by ApplicationTablesComponent
      const tablesKey = ['applicationTables', id, page, pageSize];

      queryClient.prefetchQuery({
        queryKey: tablesKey,
        queryFn: async ({ signal }) => {
          const url = `/arcee/application/table/list/?application_id=${id}&page=${page}&page_size=${pageSize}`;
          const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
          if (!resp.ok) {
            let body = {};
            try { body = await resp.json(); } catch (e) { }
            throw new Error(body?.error || `Failed to fetch application tables (${resp.status})`);
          }
          return resp.json();
        },
        staleTime: 30_000,
      });
    },
  });

  const handleDelete = () => {
    showAlertDialog({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete application "${app.name}"?`,
      type: 'info',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setShowProgress(true);
        setDeleting(true);
        try {
          const resp = await apiHelper.apiCall('/arcee/application/delete/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ application_id: app.id }),
          });

          if (!resp.ok) {
            const errData = await resp.json();
            showAlertDialog({
              title: 'Deletion Failed',
              message: errData.error || errData.message || errData.details || 'An error occurred during deletion.',
              type: 'error',
              okLabel: 'Close',
              showCancel: false,
            });
            return;
          }

          const data = await resp.json();

          if (data.status === 'failed') {
            showAlertDialog({
              title: 'Deletion Failed',
              message: data.message || 'Failed to delete the application.',
              type: 'error',
              okLabel: 'Close',
              showCancel: false,
            });
            return;
          }

          showAlertDialog({
            title: 'Success',
            message: data.message || 'Application deleted successfully.',
            type: 'success',
            okLabel: 'Close',
            showCancel: false,
            onOk: () => {
              // redirect to list or home page after deletion
              window.location.href = '/dashboard/applications';
            },
          });
        } catch (error) {
          showAlertDialog({
            title: 'Error',
            message: error.message || 'An error occurred during deletion.',
            type: 'error',
            okLabel: 'Close',
            showCancel: false,
          });
        } finally {
          setShowProgress(false);
          setDeleting(false);
        }
      },
      onCancel: () => { },
    });
  };

  const handleExportAsPackage = () => {
    showAlertDialog({
      title: 'Export as Package',
      message: `Are you sure you want to export application "${app.name}" as a package?`,
      type: 'info',
      okLabel: 'Export',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setShowProgress(true);
        try {
          const resp = await apiHelper.apiCall('/arcee/application/export-to-pack/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ application_id: app.id }),
          });

          if (!resp.ok) {
            const errData = await resp.json();
            showAlertDialog({
              title: 'Export Failed',
              message: errData.error || errData.message || 'An error occurred during export.',
              type: 'error',
              okLabel: 'Close',
              showCancel: false,
            });
            return;
          }

          const data = await resp.json();
          showAlertDialog({
            title: 'Success',
            message: data.message || 'Application exported successfully.',
            type: 'success',
            okLabel: 'Close',
            showCancel: false,
            onOk: () => {
              navigate('/dashboard/my-packages');
            },
          });
        } catch (error) {
          showAlertDialog({
            title: 'Error',
            message: error.message || 'An error occurred during export.',
            type: 'error',
            okLabel: 'Close',
            showCancel: false,
          });
        } finally {
          setShowProgress(false);
        }
      },
      onCancel: () => { },
    });
  };

  const handleEdit = () => {
    navigate(`/dashboard/applications/${id}/update`, { state: { app } });
  }

  const handleCheckUpdate = () => {
    setShowUpdateModal(true);
  }

  const handleRollback = () => {
    setShowRollbackModal(true);
  }

  const handleUsers = () => {
    navigate(`/dashboard/applications/${id}/users`);
  }

  const handleRoles = () => {
    navigate(`/dashboard/applications/${id}/roles`);
  }

  const handlePaymentAnalytics = () => {
    navigate(`/dashboard/applications/${id}/payment-analytics`);
  }

  const handlePaymentTrace = () => {
    navigate(`/dashboard/applications/${id}/payment-trace`);
  }

  // Only an application that has the certificate_template table (the AthloHive
  // pack, after update_athlohive_pack_v3_certificate_tables) gets the button;
  // every other application never sees it.
  const { data: hasCertificateTemplates } = useQuery({
    queryKey: ['hasCertificateTemplateTable', id],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${id}&search=certificate_template&page_size=20`,
        { method: 'GET', signal },
      );
      if (!resp.ok) return false;
      const body = await resp.json();
      return (body.results || body || []).some((table) => table.name === 'certificate_template');
    },
    enabled: !!id,
    staleTime: 60_000,
  });

  const handleCertificateTemplates = () => {
    navigate(`/dashboard/applications/${id}/certificate-templates`);
  }

  const handleNotificationAnalytics = () => {
    navigate(`/dashboard/applications/${id}/notification-analytics`);
  }

  const handleNotificationControls = () => {
    navigate(`/dashboard/applications/${id}/notification-controls`);
  }

  const handleEventHandling = () => {
    navigate(`/dashboard/applications/${id}/events`);
  }

  const handleWebhooks = () => {
    navigate(`/dashboard/applications/${id}/webhooks`);
  }

  // Deployment configuration, not the application's name: a rename must not
  // turn the Events screens on or off. See utils/eventsAccess.js.
  const showEvents = isEventsEnabledForApplication(id, envConfig.eventsAllowedApplicationId);

  const handleEvents = () => {
    navigate(`/dashboard/applications/${id}/athlohive/events`);
  }

  const handlePublicPages = () => {
    navigate(`/dashboard/applications/${id}/public-pages`);
  }

  const handlePageBuilder = () => {
    navigate(`/dashboard/applications/${id}/page-builder`);
  }

  useEffect(() => {
    setLoading(Boolean(appDetailsLoading || appDetailsFetching));
    if (appDetailsError) {
      setErrorInfo({
        message: appDetailsError?.message || String(appDetailsError),
        type: 'error'
      });
      setApp(null);
      setLoading(false);
      return;
    }

    if (appDetailsData) {
      setApp(appDetailsData);
      setErrorInfo(null);
    }
  }, [appDetailsData, appDetailsError, appDetailsLoading, appDetailsFetching]);

  useEffect(() => {
    if (appDetailsError) {
      setErrorInfo({ message: appDetailsError?.message || String(appDetailsError), type: 'error' });
      setLoading(false);
    }
  }, [appDetailsError]);


  const gradientTopBar = {
    width: '100%',
    height: '8px',
    borderTopLeftRadius: theme.cardRadius,
    borderTopRightRadius: theme.cardRadius,
    background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border" style={{ color: theme.primary, width: '3rem', height: '3rem' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (errorInfo) {
    return (
      <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
        <MessageBox messageType={errorInfo.type || 'error'} message={errorInfo.message || 'Something went wrong. Please try again.'} />
      </div>
    );
  }

  if (!app) return null;

  return (
    <div className="d-flex flex-column w-100" style={{ background: "#f8fafc", minHeight: '100vh', paddingBottom: '4rem' }}>

      {/* Hero Header */}
      <div className="w-100 px-4 py-5 mb-4" style={{ background: '#fff', borderBottom: '4px solid', borderImage: `linear-gradient(to right, ${theme.primary}, ${theme.secondary}) 1` }}>
        <div className="container-fluid" style={{ maxWidth: 1400 }}>
          {/* Back navigation */}
          {/* Note: I am not including the Back button block here as it was earlier in the file, but in the previous view it was suspiciously missing from the 260-270 range. Wait. 
          In the previous view (Step 303), Lines 264-265 were:
            <div className="w-100 px-4 py-5 mb-4" ...>
            </div>
          IT WAS CLOSED IMMEDIATELY at line 265! This is the bug.
          I need to replace lines 264-265 completely and reopen specific containers.
          */}

          <div className="mb-4">
            <button
              onClick={() => navigate('/dashboard/applications')}
              className="btn btn-link text-decoration-none d-flex align-items-center"
              style={{ color: '#64748b', fontSize: '14px', fontWeight: 500, padding: '8px 16px', borderRadius: theme.buttonRadius || '6px', transition: 'all 0.2s' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.background = theme.primary;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = '#64748b';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Applications
            </button>
          </div>

          <div className="d-flex flex-column flex-lg-row align-items-start gap-4">
            {/* Logo */}
            {app.logo ? (
              <img
                src={app.logo}
                alt={app.name}
                style={{
                  height: 80,
                  width: 80,
                  objectFit: 'contain',
                  borderRadius: theme.cardRadius,
                  background: '#f8f9fa',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
                  padding: '8px'
                }}
              />
            ) : (
              <div style={{
                height: 80,
                width: 80,
                borderRadius: theme.cardRadius,
                background: theme.primary + '15',
                color: theme.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}>
                <i className="bi bi-grid-fill"></i>
              </div>
            )}

            <div className="flex-grow-1">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                <h1 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '2rem', letterSpacing: '-0.5px' }}>
                  {app.name}
                </h1>
                <span
                  style={{
                    fontSize: '13px',
                    padding: '6px 14px',
                    borderRadius: '30px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: app.status === 'published' ? '#dcfce7' : '#fff7ed',
                    color: app.status === 'published' ? '#166534' : '#c2410c'
                  }}
                >
                  {app.status}
                </span>

                <span
                  style={{
                    fontSize: '13px',
                    padding: '6px 14px',
                    borderRadius: '30px',
                    fontWeight: 600,
                    background: '#f1f5f9',
                    color: '#475569'
                  }}
                  title={app.exported_as_pack ? "This application has been exported as a package" : (app.installed_from_pack ? "This application was installed from a package" : "Unexported custom application")}
                >
                  v{app.current_version_code || 1}
                </span>

                <span
                  style={{
                    fontSize: '13px',
                    padding: '6px 14px',
                    borderRadius: '30px',
                    fontWeight: 600,
                    background: '#f1f5f9',
                    color: '#475569'
                  }}
                  title="Display version string"
                >
                  v{app.version || '1.0.0'}
                </span>

                {app.update_available && (
                  <span
                    role="button"
                    style={{
                      fontSize: '13px',
                      padding: '6px 14px',
                      borderRadius: '30px',
                      fontWeight: 600,
                      background: '#fef3c7',
                      color: '#92400e',
                      cursor: 'pointer',
                    }}
                    title="A newer version of the source package is available"
                    onClick={handleCheckUpdate}
                  >
                    Update Available
                  </span>
                )}

                {envConfig.showApplicationRollback && app.rollback_available && (
                  <span
                    role="button"
                    style={{
                      fontSize: '13px',
                      padding: '6px 14px',
                      borderRadius: '30px',
                      fontWeight: 600,
                      background: '#fee2e2',
                      color: '#991b1b',
                      cursor: 'pointer',
                    }}
                    title="Undo the most recent update applied to this application"
                    onClick={handleRollback}
                  >
                    Rollback Available
                  </span>
                )}
              </div>
              {app.description && (
                <p className="text-muted mb-4" style={{ maxWidth: '800px', fontSize: '16px', lineHeight: '1.6', wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                  {app.description}
                </p>
              )}

              {/* Action Bar */}
              <div className="d-flex flex-wrap gap-3">
                <ActionButton label="Export as Package" icon="bi-box-arrow-up" color={theme.primary} onClick={handleExportAsPackage} theme={theme} />
                <ActionButton label="Check for Updates" icon="bi-arrow-repeat" color={theme.primary} onClick={handleCheckUpdate} theme={theme} />
                {envConfig.showApplicationRollback && app.rollback_available && (
                  <ActionButton label="Rollback" icon="bi-arrow-counterclockwise" color="#ef4444" onClick={handleRollback} theme={theme} />
                )}
                <ActionButton label="Edit Application" icon="bi-pencil" color={theme.primary} onClick={handleEdit} theme={theme} />
                {showEvents && (
                  <ActionButton label="Events" icon="bi-calendar-event" color={theme.primary} onClick={handleEvents} theme={theme} />
                )}
                <ActionButton label="Manage Users" icon="bi-people" color="#475569" onClick={handleUsers} theme={theme} />
                <ActionButton label="Manage Roles" icon="bi-shield-lock" color="#475569" onClick={handleRoles} theme={theme} />
                <ActionButton label="Payment Analytics" icon="bi-cash-stack" color="#475569" onClick={handlePaymentAnalytics} theme={theme} />
                <ActionButton label="Payment Trace" icon="bi-search" color="#475569" onClick={handlePaymentTrace} theme={theme} />
                {hasCertificateTemplates && (
                  <ActionButton label="Certificate Templates" icon="bi-award" color="#475569" onClick={handleCertificateTemplates} theme={theme} />
                )}
                <ActionButton label="Notification Analytics" icon="bi-graph-up" color="#475569" onClick={handleNotificationAnalytics} theme={theme} />
                <ActionButton label="Notification Controls" icon="bi-bell" color="#475569" onClick={handleNotificationControls} theme={theme} />
                <ActionButton label="Event Framework" icon="bi-lightning-charge" color="#475569" onClick={handleEventHandling} theme={theme} />
                <ActionButton label="Webhook Endpoints" icon="bi-send-check" color="#475569" onClick={handleWebhooks} theme={theme} />
                <ActionButton label="Public Pages" icon="bi-globe2" color="#475569" onClick={handlePublicPages} theme={theme} />
                <ActionButton label="Page Builder" icon="bi-layout-text-window-reverse" color="#475569" onClick={handlePageBuilder} theme={theme} />
                {envConfig.showApplicationDelete && (
                  <div className="ms-auto-lg"> {/* Spacer for desktop */}
                    <ActionButton label="Delete" icon="bi-trash" color="#ef4444" isDelete onClick={handleDelete} theme={theme} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attributes Strip */}
      <div className="w-100 mb-4 px-4">
        <div className="container-fluid" style={{ maxWidth: 1400, padding: 0 }}>
          <div className="row g-3">
            {ATTRIBUTES.map(attr => {
              let value = app[attr.key];
              if (typeof value === 'object' && value !== null && 'name' in value) value = value.name;
              if (!value && value !== 0) return null; // Skip empty

              return (
                <div className="col-12 col-sm-6 col-md-4 col-lg-2" key={attr.key}>
                  <div className="p-3 bg-white h-100 d-flex flex-column justify-content-center shadow-sm" style={{ border: `1px solid ${theme.primary}40`, borderRadius: theme.cardRadius }}>
                    <div className="d-flex align-items-center mb-1" style={{ fontSize: '12px', fontWeight: 600, color: theme.primary }}>
                      <i className={`bi ${attr.icon} me-2`} style={{ fontSize: '14px' }}></i>
                      {attr.label}
                    </div>
                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: '14px' }} title={attr.formatter ? attr.formatter(value) : value}>
                      {attr.formatter ? attr.formatter(value) : value}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container-fluid px-4" style={{ maxWidth: 1400 }}>
        <div className="row g-4">
          {/* Sidebar Info (swapped to Left) */}
          <div className="col-12 col-lg-4">
            <div className="bg-white shadow-sm border p-4" style={{ borderRadius: theme.cardRadius }}>
              <h6 className="fw-bold text-uppercase text-muted mb-4" style={{ fontSize: '12px', letterSpacing: '1px' }}>
                Quick Actions
              </h6>
              <div className="d-grid gap-3">
                <button
                  className="btn p-3 d-flex align-items-center text-start"
                  onClick={handleEdit}
                  style={{
                    borderRadius: theme.buttonRadius || '8px',
                    border: `1px solid ${theme.primary}`,
                    color: theme.primary,
                    transition: 'all 0.2s',
                    background: 'transparent'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.primary;
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.primary;
                  }}
                >
                  <i className="bi bi-pencil-square me-3 fs-5"></i>
                  <div>
                    <div className="fw-semibold">Edit Details</div>
                    <div className="small opacity-75">Update name, description, logo</div>
                  </div>
                </button>
                <button
                  className="btn p-3 d-flex align-items-center text-start"
                  onClick={handleUsers}
                  style={{
                    borderRadius: theme.buttonRadius || '8px',
                    border: `1px solid ${theme.secondary}`,
                    color: theme.secondary,
                    transition: 'all 0.2s',
                    background: 'transparent'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.secondary;
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.secondary;
                  }}
                >
                  <i className="bi bi-people me-3 fs-5"></i>
                  <div>
                    <div className="fw-semibold">Manage Users</div>
                    <div className="small opacity-75">Assign users to this app</div>
                  </div>
                </button>
              </div>

              {/* Configuration */}
              <div className="mt-4 pt-4 border-top">
                <h6 className="fw-bold text-uppercase text-muted mb-3" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  Configuration
                </h6>
                <div className="d-grid gap-2">
                  <button
                    className="btn p-2 d-flex align-items-center text-start text-dark"
                    onClick={() => navigate(`/dashboard/applications/${id}/group-types`)}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-diagram-2 me-2 text-primary"></i>
                    <span className="small fw-semibold">Group Types</span>
                  </button>
                  <button
                    className="btn p-2 d-flex align-items-center text-start text-dark"
                    onClick={() => navigate(`/dashboard/applications/${id}/groups`)}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-diagram-3 me-2 text-primary"></i>
                    <span className="small fw-semibold">Organization Structure</span>
                  </button>
                  <button
                    className="btn p-2 d-flex align-items-center text-start text-dark"
                    onClick={handleNotificationAnalytics}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-graph-up me-2 text-primary"></i>
                    <span className="small fw-semibold">Notification Analytics</span>
                  </button>
                  <button
                    className="btn p-2 d-flex align-items-center text-start text-dark"
                    onClick={handleNotificationControls}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-bell me-2 text-primary"></i>
                    <span className="small fw-semibold">Notification Controls</span>
                  </button>
                  <button
                    className="btn p-2 d-flex align-items-center text-start"
                    onClick={handleEventHandling}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-lightning-charge-fill me-2"></i>
                    <span className="small fw-semibold">Event Framework</span>
                  </button>
                  <button
                    className="btn p-2 d-flex align-items-center text-start text-dark"
                    onClick={handleWebhooks}
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-send-check me-2 text-primary"></i>
                    <span className="small fw-semibold">Webhook Endpoints</span>
                  </button>
                  <button
                    onClick={handlePublicPages}
                    className="btn p-2 d-flex align-items-center text-start"
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px', color: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#ecfeff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-globe2 me-2" style={{ color: '#0891b2' }}></i>
                    <span className="small fw-semibold">Public Pages</span>
                  </button>
                  <button
                    onClick={handlePageBuilder}
                    className="btn p-2 d-flex align-items-center text-start"
                    style={{ background: 'transparent', border: 'none', transition: 'all 0.2s', borderRadius: theme.buttonRadius || '6px', color: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-layout-text-window-reverse me-2" style={{ color: '#7c3aed' }}></i>
                    <span className="small fw-semibold">Page Builder</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Banner Image Preview if exists */}
            {app.banner_image && (
              <div className="mt-4 bg-white shadow-sm border overflow-hidden" style={{ borderRadius: theme.cardRadius }}>
                <div style={{
                  height: 200,
                  width: '100%',
                  background: `url('${app.banner_image}') center/cover no-repeat`,
                }} />
                <div className="p-3 text-center text-muted small">
                  App Banner Image
                </div>
              </div>
            )}
          </div>

          {/* Right column — tabbed panel */}
          <div className="col-12 col-lg-8">
            {/* Tab strip */}
            <div
              className="d-flex gap-1 mb-4"
              style={{
                borderBottom: '2px solid #e2e8f0',
                paddingBottom: 0,
              }}
            >
              {[
                { key: 'tables', label: 'Tables',       icon: 'bi-table' },
                { key: 'files',  label: 'File Manager', icon: 'bi-folder2-open' },
              ].map(tab => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="btn d-flex align-items-center gap-2"
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      color: active ? (theme?.primaryColor || '#1e293b') : '#64748b',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 0,
                      borderBottom: active
                        ? `2.5px solid ${theme?.primaryColor || '#1e293b'}`
                        : '2.5px solid transparent',
                      marginBottom: -2,
                      padding: '8px 14px',
                      transition: 'color 0.15s',
                    }}
                  >
                    <i className={`bi ${tab.icon}`} style={{ fontSize: 14 }} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {activeTab === 'tables' && (
              <div className="mb-4">
                <ApplicationTablesComponent applicationId={app.id} />
              </div>
            )}

            {activeTab === 'files' && (
              <div className="mb-4">
                <FileBrowserComponent applicationId={app.id} />
              </div>
            )}
          </div>
        </div>
      </div>

      <ProgressBarDialog
        open={showProgress}
        text_message="Deleting Application..."
        cancellable={false}
      />

      {showUpdateModal && (
        <UpdateApplicationModal
          theme={theme}
          applicationId={id}
          onClose={() => setShowUpdateModal(false)}
          onApplied={() => queryClient.invalidateQueries(appDetailsQueryKey)}
        />
      )}

      {showRollbackModal && (
        <RollbackApplicationModal
          theme={theme}
          applicationId={id}
          currentVersionCode={app.current_version_code}
          onClose={() => setShowRollbackModal(false)}
          onRolledBack={() => queryClient.invalidateQueries(appDetailsQueryKey)}
        />
      )}
    </div >
  );
}

function ActionButton({ label, icon, color, isDelete, onClick, filled, theme }) {
  const baseStyle = {
    padding: '10px 20px',
    borderRadius: theme?.buttonRadius || '8px',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.2s',
    border: filled ? 'none' : `1px solid ${isDelete ? '#fecaca' : '#e2e8f0'}`,
    background: filled ? color : (isDelete ? '#fef2f2' : '#fff'),
    color: filled ? '#fff' : color,
  };

  return (
    <button
      onClick={onClick}
      className="d-flex align-items-center gap-2 app-action-btn"
      style={baseStyle}
      onMouseEnter={e => {
        if (!filled) e.currentTarget.style.background = isDelete ? '#fee2e2' : '#f8fafc';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        if (!filled) e.currentTarget.style.background = isDelete ? '#fef2f2' : '#fff';
        e.currentTarget.style.transform = 'none';
      }}
    >
      <i className={`bi ${icon}`} style={{ fontSize: '16px' }}></i>
      {label}
    </button>
  );
}

function formatDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
